import { useQuery } from '@tanstack/react-query';
import queryClient from '../../query_client.js';
import { fetchJson } from '../helpers/fetch.js';
import {
  option_translation_path,
  option_set_translations_path,
  option_set_hierarchy_translation_scopes_path
} from '~/routes';

const hierarchyTranslationScopesQueryKey = (hierarchyKey, locale) => ['optionSetHierarchyTranslationScopes', hierarchyKey, locale];
const scopeQueryKey = (scope, locale) => ['optionSetTranslations', scope, locale];
const optQueryKey = (scope, key, locale) => ['optionTranslation', scope, key, locale];
const pickLabel = (labels, value) => labels?.[value] ?? value;

const SCOPE_DELIM = '\u001F';

const scopeFromValue = (value) => {
  if (typeof value !== 'string') return null;
  if (!value.includes(SCOPE_DELIM)) return null;
  return value.split(SCOPE_DELIM, 2)[0];
}

const unscopedValue = (value) => {
  if (typeof value !== 'string') return value;
  if (!value.includes(SCOPE_DELIM)) return value;
  return value.split(SCOPE_DELIM, 2)[1];
}

// Asynchronously load translations for an option set before synchronously calling getOptLabel.
// Required for example for ReactSelect unless custom renderes are used
export async function load({ scope, hierarchyKey, locale = I18n.locale }) {
  if (!locale) return {};

  if (hierarchyKey) {
    const { scopes = [] } = await queryClient.fetchQuery({
      queryKey: hierarchyTranslationScopesQueryKey(hierarchyKey, locale),
      queryFn: () => fetchJson(option_set_hierarchy_translation_scopes_path({ option_set_hierarchy_key: hierarchyKey })),
    });

    await Promise.all(scopes.map(s => load({ scope: s, locale })));

    return {};
  }

  if (!scope) return {};

  return await queryClient.fetchQuery({
    queryKey: scopeQueryKey(scope, locale),
    queryFn: () => fetchJson(option_set_translations_path({ option_locale: locale, option_set_key: scope })),
  });
}

export function t({ scope, value, locale = I18n.locale }) {
  const labels = queryClient.getQueryData(scopeQueryKey(scope, locale)) || {};
  return pickLabel(labels, value);
}

// Use option set translations in function components
export function useOptScope({ scope, locale = I18n.locale, enabled = true }) {
  const query = useQuery({
    queryKey: scopeQueryKey(scope, locale),
    enabled: enabled && !!scope && !!locale,
    queryFn: () => fetchJson(option_set_translations_path({ option_locale: locale, option_set_key: scope })),
  });

  const getLabel = (value) => pickLabel(query.data, value);

  return {
    labels: query.data || {},
    getLabel,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
}

// Use a single option translation in function components
export function useOpt({ scope, key, locale = I18n.locale, enabled = true }) {
  const query = useQuery({
    queryKey: optQueryKey(scope, key, locale),
    enabled: enabled && !!scope && !!key && !!locale,
    queryFn: () => fetchJson(option_translation_path({ option_locale: locale, option_set_key: scope, translation_key: key })),
  });

  return {
    value: query.data?.value ?? key ?? '',
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
}

const Opt = {
  load,
  t,
  useOptScope,
  useOpt,
  scopeFromValue,
  unscopedValue,
};

export default Opt;